import type React from "react";
import { useEffect, useState } from "react";

import { Stack, Typography } from "@mui/material";
import { startOfDay, startOfToday } from "date-fns";
import { useTranslation } from "react-i18next";

import { BaseButton } from "shared/atoms/BaseButton";
import { DatePicker } from "shared/molecules/DatePicker";
import { useLocalizedDate } from "utils/date";

interface Props {
  readonly defaultDate: string;
  readonly isEditingDateEnabled: boolean;
  readonly open: boolean;
  readonly onChange: (date: string) => void;
}

export const MedicalNoteDate: React.FC<Props> = ({ defaultDate, isEditingDateEnabled, open, onChange }) => {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const { format, parseISO, sub } = useLocalizedDate();
  const [editedDate, setEditedDate] = useState(parseISO(defaultDate));

  useEffect(() => {
    if (editing && !open) {
      setEditing(false);
    }
  }, [open]);

  if (!defaultDate) {
    return null;
  }

  return (
    <Stack direction="row" alignItems="center">
      {editing ? (
        <DatePicker
          initialDate={parseISO(defaultDate)}
          range={[startOfDay(sub(parseISO(defaultDate), { years: 1 })), startOfToday()]}
          onChanged={date => onChange(date.toISOString())}
        />
      ) : (
        <Typography variant="subtitle1" data-testid="noteStartTime">
          {format(parseISO(defaultDate), "PP")}
        </Typography>
      )}

      {open && isEditingDateEnabled && !editing && (
        <BaseButton
          data-testid="editNoteStartTimeButton"
          variant="text"
          icon="pen-to-square"
          size="small"
          style={{ flexGrow: 0, flexBasis: "auto", minHeight: "auto", marginBottom: "3px" }}
          onClick={event => {
            setEditing(true);
            setEditedDate(parseISO(defaultDate));
            event.stopPropagation();
          }}
        />
      )}

      {open && editing && (
        <BaseButton
          variant="text"
          size="small"
          text={t("buttons.cancel")}
          type="button"
          uppercase
          onClick={event => {
            onChange(editedDate.toISOString());
            setEditing(false);
            event.stopPropagation();
          }}
        />
      )}
    </Stack>
  );
};
